import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFinance } from '@/hooks/useFinance';
import { useMerchantClients } from '@/hooks/useMerchantClients';
import { formatDate, formatMoney } from '@/lib/finance';
import { REVENUE_SOURCE_META } from '@/lib/financeSync';
import { FinanceStatCard, FinanceSection, FinanceEmpty } from '@/pages/dashboard/finance/components/FinanceUI';

const ORDER_STATUS_LABEL: Record<string, string> = {
  'En attente': 'En attente',
  'Confirmée': 'Confirmée',
  'Préparation': 'Préparation',
  'Expédiée': 'Expédiée',
  'Livrée': 'Livrée',
  'Annulée': 'Annulée',
};

function orderAmount(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Onglet « Clients » de Zifek Finance.
 * Réutilise la MÊME source de données que l'app Gestion Clients
 * (hook partagé useMerchantClients) : les chiffres sont donc identiques
 * partout, et les revenus des commandes peuvent être rattachés à la
 * trésorerie en un clic.
 */
export default function FinanceClientsPage() {
  const finance = useFinance();
  const { currency, orders } = finance;
  const { clients, loading: clientsLoading, error: clientsError, reload, stats } = useMerchantClients();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await finance.syncAllRevenues();
      setSyncMsg(
        res.created > 0
          ? `${res.created} revenu(s) rattaché(s) à la trésorerie (commandes + hébergement).`
          : 'Tous les revenus payés (commandes + hébergement) sont déjà synchronisés.',
      );
    } catch (err: unknown) {
      setSyncMsg(err instanceof Error ? err.message : 'Erreur lors de la synchronisation.');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q),
    );
  }, [clients, search]);

  const paidOrders = orders.filter((o) => orderAmount(o.sommepayee) > 0);
  const orderRevenue = paidOrders.reduce((s, o) => s + orderAmount(o.sommepayee), 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label="Clients" value={String(stats.count)} icon="ri-user-line" tone="secondary" hint="Clients uniques" />
        <FinanceStatCard label="Chiffre d'affaires clients" value={formatMoney(stats.totalSpent, currency)} icon="ri-money-dollar-circle-line" tone="primary" hint="Commandes + paiements" />
        <FinanceStatCard label="Panier moyen" value={formatMoney(stats.average, currency)} icon="ri-bar-chart-line" tone="accent" hint="Par client" />
        <FinanceStatCard label="Commandes payées" value={String(paidOrders.length)} icon="ri-file-list-3-line" tone="secondary" hint={`${formatMoney(orderRevenue, currency)} encaissés`} />
      </div>

      {/* Synchronisation trésorerie */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-link-m text-base"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">Synchronisation des revenus</h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                Les revenus payés de tous les modules (produits, produits digitaux, services, formations, rendez-vous et réservations d'hébergement) sont rattachés à la trésorerie — une écriture par source, sans doublon.
              </p>
            </div>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            {syncing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-refresh-line"></i>}
            Synchroniser
          </button>
        </div>
        {syncMsg && (
          <div className="mt-3 px-3 py-2 rounded-lg text-xs bg-secondary-50 text-secondary-800 flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>{syncMsg}
          </div>
        )}
      </div>

      {/* Revenus regroupés par source */}
      <FinanceSection title="Revenus par source" icon="ri-pie-chart-2-line">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {REVENUE_SOURCE_META.map((meta) => {
            const amount = finance.revenueBySource[meta.label] || 0;
            return (
              <div key={meta.key} className="bg-background-50 border border-background-200/70 rounded-lg p-3.5 flex items-center gap-3">
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                  <i className={meta.icon}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500 truncate">{meta.label}</p>
                  <p className="text-sm font-bold text-foreground-950">{formatMoney(amount, currency)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </FinanceSection>

      {/* Liste des clients */}
      <FinanceSection
        title={`Clients (${clients.length})`}
        icon="ri-team-line"
        action={
          <div className="relative w-full sm:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un client"
              className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            />
          </div>
        }
      >
        {clientsLoading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : clientsError ? (
          <div className="flex flex-col items-center py-10 text-center">
            <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
            <p className="text-sm text-foreground-600 mb-3">{clientsError}</p>
            <button onClick={reload} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors">
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <FinanceEmpty icon="ri-user-line" title="Aucun client" hint="Vos clients apparaîtront ici après leurs premiers achats." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold text-foreground-500 uppercase tracking-wider border-b border-background-200/70">
                  <th className="text-left py-2.5 pr-4">Client</th>
                  <th className="text-left py-2.5 pr-4 hidden md:table-cell">Contact</th>
                  <th className="text-center py-2.5 pr-4">Commandes</th>
                  <th className="text-right py-2.5 pr-4">Total dépensé</th>
                  <th className="text-right py-2.5 pr-4 hidden lg:table-cell">Dernière activité</th>
                  <th className="text-center py-2.5 w-[50px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {filtered.map((c) => (
                  <tr key={c.clientId} className="hover:bg-background-50/60 transition-colors">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-accent-700">{(c.name || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-foreground-900 block truncate">
                            {c.name || (c.clientId.startsWith('guest:') ? c.clientId.slice(6) : `Client #${c.clientId}`)}
                          </span>
                          {!c.registered && (
                            <span className="text-[10px] text-foreground-400">Invité / non inscrit</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 hidden md:table-cell">
                      <div className="text-xs text-foreground-500 space-y-0.5">
                        {c.email && <div className="flex items-center gap-1"><i className="ri-mail-line text-foreground-400"></i>{c.email}</div>}
                        {c.phone && <div className="flex items-center gap-1"><i className="ri-phone-line text-foreground-400"></i>{c.phone}</div>}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-center font-semibold text-foreground-800">{c.totalOrders}</td>
                    <td className="py-3 pr-4 text-right font-semibold text-primary-600 whitespace-nowrap">{formatMoney(c.totalSpent, currency)}</td>
                    <td className="py-3 pr-4 text-right text-foreground-500 hidden lg:table-cell whitespace-nowrap">
                      {c.lastActivity ? formatDate(c.lastActivity) : '—'}
                    </td>
                    <td className="py-3 text-center">
                      {/^\d+$/.test(c.clientId) && (
                        <button
                          onClick={() => navigate(`/dashboard/customers-manager/${c.clientId}`)}
                          className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer mx-auto"
                          title="Voir la fiche client"
                        >
                          <i className="ri-arrow-right-line text-primary-600 text-sm"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </FinanceSection>

      {/* Commandes regroupées */}
      <FinanceSection title={`Commandes récentes (${orders.length})`} icon="ri-file-list-3-line">
        {orders.length === 0 ? (
          <FinanceEmpty icon="ri-file-list-3-line" title="Aucune commande" hint="Les commandes de votre boutique apparaîtront ici." />
        ) : (
          <div className="divide-y divide-background-200/70">
            {orders.slice(0, 12).map((o) => {
              const paid = orderAmount(o.sommepayee);
              const total = orderAmount(o.totalttc) || paid;
              return (
                <div key={o.id} className="flex items-center gap-3 py-3">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${paid > 0 ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}>
                    <i className={paid > 0 ? 'ri-check-double-line' : 'ri-time-line'}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground-900 truncate">
                      #{o.id} · {o.nomclient || 'Client'}
                    </div>
                    <div className="text-xs text-foreground-400 flex items-center gap-2 flex-wrap">
                      <span>{o.date_time ? formatDate(o.date_time) : '—'}</span>
                      <span className={`px-1.5 py-0.5 rounded-full ${paid > 0 ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-500'}`}>
                        {ORDER_STATUS_LABEL[o.statut_com_vendeur || ''] || o.statut_com_vendeur || 'En attente'}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{formatMoney(total, o.monaie || currency)}</span>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      <p className="text-[11px] text-foreground-400 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5"></i>
        Les données clients et commandes proviennent de la même source que votre boutique : toute mise à jour apparaît automatiquement ici.
      </p>
    </div>
  );
}